import React from 'react';
import {
  Button,
  SelectDropDown,
  TextField,
  AutoComplete,
} from '@/components/Common';
import {
  IconPosition,
  TextfieldType,
} from '@/components/Common/TextField/TextField.types';
import { FieldError } from 'react-hook-form';
import { Modal } from '@/components/Layout';
import { useEstablishmentFormLogic } from '@/components/Views/Establishments/useEstablishmentFormLogic';

export const EstablishmentForm = () => {
  const {
    openEstablishmentModal,
    isOpenEstablishmentEditModal,
    isSubmitting,
    isEditMode,
    establishmentTypeOptions,
    regionOptions,
    schoolSuggestions,
    establishmentNameValue,
    isExistingSchool,
    hasUserFinishedTyping,
    selectedEstablishmentType,
    selectedCategory,
    selectedRegion,
    categoryOptions,
    handleSubmit,
    register,
    errors,
    setValue,
    handleCloseModal,
    handleChangeEstablishmentType,
    handleChangeRegion,
    handleChangeCategory,
    searchSchool,
    handleSchoolChange,
    handleSchoolBlur,
    onSubmit,
    watch,
  } = useEstablishmentFormLogic();

  const isModalOpen = openEstablishmentModal || isOpenEstablishmentEditModal;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      headerStyle="bg-carbon h-[36px]"
      modalStyle="bg-carbon"
    >
      <div className="flex flex-col justify-center items-center mb-7">
        <span className="text-xl text-primary font-bold">
          {isEditMode ? "Modifier l'établissement" : "Ajouter l'établissement"}
        </span>
        <span className="text-xs text-mediumGrey font-normal">
          Informations sur l'établissement
        </span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="w-[653px]">
        <div className="mb-4 relative">
          <AutoComplete<string>
            value={establishmentNameValue}
            suggestions={schoolSuggestions.map((s) => s.label)}
            completeMethod={searchSchool}
            onChange={handleSchoolChange}
            onBlur={handleSchoolBlur}
            placeholder="Nom de l'établissement"
            forceSelection={false}
            className="h-full"
            disabled={isEditMode}
          />
          {(errors.establishmentName as FieldError)?.message && (
            <span className="text-red-500 text-xs">
              {(errors.establishmentName as FieldError)?.message}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 mt-2">
          {/* Colonne gauche */}
          <div className="space-y-4">
            <div className="h-[60px]">
              <SelectDropDown
                key={`establishment-type-${isEditMode ? selectedEstablishmentType?._id || 'edit' : 'create'}`}
                options={establishmentTypeOptions}
                placeholder="Type d'établissement"
                name="establishmentType"
                handleChange={handleChangeEstablishmentType}
                setValue={setValue}
                initialSelectedOptions={
                  isEditMode && selectedEstablishmentType
                    ? selectedEstablishmentType
                    : null
                }
                helpText={(errors.establishmentType as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                className="!text-white font-normal"
                selectDropDownStyle={{
                  backgroundColor: '#232323',
                  fontSize: '12px',
                }}
              />
            </div>

            <div className="h-[60px]">
              <TextField
                placeholder="Adresse"
                register={register('address')}
                helpText={(errors.address as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                textFieldType={TextfieldType.INPUT}
                className="!bg-almostBlack text-xs text-white font-normal"
                iconPosition={IconPosition.LEFT}
              />
            </div>

            <div className="h-[60px]">
              <TextField
                placeholder="Crédits IA"
                register={register('creditsIA', { valueAsNumber: true })}
                helpText={(errors.creditsIA as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                textFieldType={TextfieldType.INPUT}
                className="!bg-almostBlack text-xs text-white font-normal"
                iconPosition={IconPosition.LEFT}
              />
            </div>

            <div className="h-[60px]">
              <SelectDropDown
                options={regionOptions}
                placeholder="Région *"
                name="region"
                handleChange={handleChangeRegion}
                setValue={setValue}
                initialSelectedOptions={selectedRegion}
                disabled={!isEditMode && (isExistingSchool || !hasUserFinishedTyping)}
                helpText={(errors.region as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                className="!text-white font-normal"
                selectDropDownStyle={{
                  backgroundColor: '#232323',
                  fontSize: '12px',
                }}
              />
            </div>
          </div>

          {/* Colonne droite */}
          <div className="space-y-4">
            <div className="h-[60px]">
              <SelectDropDown
                key={`establishment-category-${isEditMode ? selectedCategory?._id || 'edit' : 'create'}`}
                options={categoryOptions}
                placeholder="Catégorie *"
                name="category"
                handleChange={handleChangeCategory}
                setValue={setValue}
                initialSelectedOptions={
                  selectedCategory ? selectedCategory : null
                }
                helpText={(errors.category as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                className="!text-white font-normal"
                selectDropDownStyle={{
                  backgroundColor: '#232323',
                  fontSize: '12px',
                }}
              />
            </div>

            <div className="h-[60px]">
              <TextField
                placeholder="Nom du responsable"
                register={register('responsibleName')}
                helpText={(errors.responsibleName as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                textFieldType={TextfieldType.INPUT}
                className="!bg-almostBlack text-xs text-white font-normal"
                iconPosition={IconPosition.LEFT}
              />
            </div>

            <div className="h-[60px]">
              <TextField
                placeholder="Email du responsable"
                register={register('responsibleEmail')}
                helpText={(errors.responsibleEmail as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                textFieldType={TextfieldType.INPUT}
                className="!bg-almostBlack text-xs text-white font-normal"
                iconPosition={IconPosition.LEFT}
              />
            </div>

            <div className="h-[60px]">
              <TextField
                placeholder="Numéro de l'établissement"
                register={register('establishmentNumber')}
                helpText={(errors.establishmentNumber as FieldError)?.message}
                errorStyle="text-red-500 text-xs"
                textFieldType={TextfieldType.INPUT}
                className="!bg-almostBlack text-xs text-white font-normal"
                iconPosition={IconPosition.LEFT}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-20">
          <Button
            type="submit"
            title={
              isSubmitting
                ? 'En cours...'
                : isEditMode
                  ? "Modifier l'établissement"
                  : "Ajouter l'établissement"
            }
            disabled={isSubmitting}
            className="py-2 px-6 rounded-lg w-[308px]"
          />
        </div>
      </form>
    </Modal>
  );
};
